import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import Konva from "konva";
import { Group } from "konva/lib/Group";

export class FloorplanItemTextKonva {
  private static readonly LABEL_TEXT_PADDING = {
    x: 3,
    y: 2,
  };

  static init(floorplanItem: FloorplanItem, fontSize: number = 16): Konva.Group {
    const textGroup = new Konva.Group({ name: "itemText" });

    const text = new Konva.Text({
      text: this.buildText(floorplanItem),
      fontSize: fontSize,
      fill: "#002766",
      x: this.LABEL_TEXT_PADDING.x,
      y: this.LABEL_TEXT_PADDING.y,
      name: "itemTextValue",
    });
    const backgroundFill = new Konva.Rect({
      fill: "#FFFFFF",
      opacity: 0.8,
      strokeEnabled: false,
    });
    const backgroundGroup = new Konva.Group({
      x: 0,
      y: 0,
      name: "itemTextBackground",
    });
    backgroundGroup.add(backgroundFill);

    textGroup.add(backgroundGroup);
    textGroup.add(text);
    this.updateBackground(textGroup);
    return textGroup;
  }

  static getText(itemText: Konva.Group): Konva.Text {
    const textNode = itemText.findOne(".itemTextValue") as Konva.Text | undefined;

    if (!textNode) {
      throw new Error("Invalid text item");
    }
    return textNode;
  }

  static refreshText(floorplanItem: FloorplanItem, textGroup: Konva.Group) {
    const itemText = textGroup.findOne(".itemTextValue") as Konva.Text;

    if (itemText instanceof Konva.Text) {
      itemText.text(this.buildText(floorplanItem));
      this.updateBackground(textGroup);
    }
  }

  static updateBackground(textGroup: Konva.Group) {
    const { backgroundFill, itemText, groupChip } = this.getBackgroundNodes(textGroup);
    const { paddingX, paddingY, capPaddingX, capPaddingY } = this.getScaledPadding(itemText);
    itemText.x(paddingX);
    itemText.y(paddingY);
    this.alignTextToPadding(itemText, textGroup, paddingY);
    const { rightEdge, bottomEdge } = this.getContentEdges(textGroup, itemText, groupChip);
    const width = rightEdge + capPaddingX;
    const height = bottomEdge + capPaddingY;
    backgroundFill.width(width);
    backgroundFill.height(height);
    backgroundFill.setAttr("cornerRadius", Math.min(width, height) * 0.1);
  }

  static scaleTextGroup(textGroup: Konva.Group, airPathGroup: Konva.Group) {
    textGroup!.scaleX(airPathGroup.scaleX());
    textGroup!.scaleY(airPathGroup.scaleY());
  }

  private static getBackgroundNodes(textGroup: Konva.Group) {
    const backgroundGroup = textGroup.findOne(".itemTextBackground") as Konva.Group | null;
    const backgroundFill = backgroundGroup?.findOne("Rect") as Konva.Rect | null;
    const itemText = textGroup.findOne(".itemTextValue") as Konva.Text | null;
    const groupChip = textGroup.findOne(".groupChip") as Group | null;

    if (!backgroundGroup || !backgroundFill || !itemText) {
      throw new Error("Invalid text item");
    }
    return { backgroundFill, itemText, groupChip };
  }

  private static getScaledPadding(itemText: Konva.Text) {
    const paddingX = Math.max(1, itemText.fontSize() * 0.2);
    const paddingY = Math.max(1, itemText.fontSize() * 0.15);
    const capPaddingX = paddingX;
    const capPaddingY = paddingY;
    return { paddingX, paddingY, capPaddingX, capPaddingY };
  }

  private static getContentEdges(textGroup: Konva.Group, itemText: Konva.Text, groupChip: Group | null) {
    const textBounds = itemText.getClientRect({ relativeTo: textGroup });
    let rightEdge = textBounds.x + textBounds.width;
    let bottomEdge = textBounds.y + textBounds.height;
    if (groupChip && groupChip.visible()) {
      const chipBounds = groupChip.getClientRect({ relativeTo: textGroup });
      rightEdge = Math.max(rightEdge, chipBounds.x + chipBounds.width);
      bottomEdge = Math.max(bottomEdge, chipBounds.y + chipBounds.height);
    }
    return { rightEdge, bottomEdge };
  }

  private static alignTextToPadding(itemText: Konva.Text, textGroup: Konva.Group, paddingY: number) {
    const textBounds = itemText.getClientRect({ relativeTo: textGroup });
    const delta = paddingY - textBounds.y;
    if (delta !== 0) {
      itemText.y(itemText.y() + delta);
    }
  }

  private static buildText(floorplanItem: FloorplanItem) {
    let text = floorplanItem.positionId;
    const displayName = floorplanItem.displayName?.trim();
    if (displayName && displayName.length > 0) {
      text += ` ${displayName}`;
    }
    return text;
  }
}
